import type { GridColDef } from '@mui/x-data-grid';
import { formatDate } from '../../dashboard/components/dateUtils';
import { parseRawFormData } from '../../dashboard/components/formUtils';
import { HandlersTarResponse } from '../../app/athenaApiGen';
import {
  getWorkflowProgress,
  WorkflowProgressValue,
} from '../../utils/workflow';

type IMCSTARApiItem = HandlersTarResponse & {
  travelDate?: string;
  eventStartDate?: string;
  workflowState?: string;
  subject?: string;
  projectName?: string;
  totalNTETripCost?: number;
  workflowProgress?: WorkflowProgressValue;
};

export interface IMCSTARRowData {
  id: string;
  tarNumber: string;
  eventStartDate: string | null;
  projectName: string;
  subject: string;
  totalNTETripCost: number | null;
  workflowState: string | null;
  workflowProgress: WorkflowProgressValue;
  workflowId: string | null;
}

const usdWithCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const FALLBACK_TEXT = '--';

const pickFirstString = (
  ...values: Array<string | null | undefined>
): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return undefined;
};

const toNumberOrNull = (value: any): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const createIMCSTARColumns = (): GridColDef<IMCSTARRowData>[] => [
  { field: 'tarNumber', headerName: 'TAR #', minWidth: 140, flex: 0.6 },
  {
    field: 'eventStartDate',
    headerName: 'Trip Start',
    minWidth: 150,
    valueGetter: (_value, row) => row.eventStartDate ?? null,
    valueFormatter: (value) => formatDate(value as string | null | undefined),
    sortable: false,
    flex: 0.6,
  },
  { field: 'projectName', headerName: 'Project', minWidth: 220, flex: 1 },
  { field: 'subject', headerName: 'Trip Title', minWidth: 220, flex: 1 },
  {
    field: 'totalNTETripCost',
    headerName: 'Est. Total',
    minWidth: 140,
    flex: 0.6,
    valueGetter: (_value, row) => {
      const rawValue = row.totalNTETripCost;
      if (rawValue === null || rawValue === undefined) return null;
      const numeric = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
      return Number.isFinite(numeric) ? numeric : null;
    },
    valueFormatter: (value) => {
      if (value === null || value === undefined) return FALLBACK_TEXT;
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return FALLBACK_TEXT;
      return usdWithCents.format(numeric);
    },
  },
  {
    field: 'workflowState',
    headerName: 'Status',
    minWidth: 200,
    flex: 0.9,
    sortable: false,
    valueFormatter: (value) => value ?? FALLBACK_TEXT,
  },
];

export const mapIMCSTARApiDataToRows = (items: IMCSTARApiItem[]): IMCSTARRowData[] =>
  items.map((it) => {
    const raw = parseRawFormData(it.rawFormData);
    const workflowState =
      pickFirstString(
        it.workflowState as string | undefined,
        raw.workflowState,
        raw?.workflow?.state,
        raw?.workflowStateLabel,
      ) || null;
    const workflowProgress =
      (it.workflowProgress as WorkflowProgressValue | undefined) ??
      (raw.workflowProgress as WorkflowProgressValue | undefined) ??
      getWorkflowProgress(workflowState ?? undefined);

    return {
      id: it.tarId ?? '',
      tarNumber: pickFirstString(it.tarNumber, raw.tarNumber) ?? FALLBACK_TEXT,
      eventStartDate:
        pickFirstString(
          it.eventStartDate,
          raw.eventStartDate,
          it.travelDate,
          raw.travelDate,
          raw?.travelDates?.start,
          raw?.travelDates?.from,
          raw?.tarTravelDates?.start,
        ) || null,
      projectName: pickFirstString(it.projectName, raw.projectName) ?? FALLBACK_TEXT,
      subject: pickFirstString(it.subject, raw.subject) ?? FALLBACK_TEXT,
      totalNTETripCost: toNumberOrNull(
        it.totalNTETripCost ??
          raw.totalNTETripCost ??
          raw.totalCost ??
          raw.totalCostOfTrip ??
          raw.tripTotal,
      ),
      workflowState,
      workflowProgress,
      workflowId: it.workflowId ?? null,
    };
  });
